import React, { useCallback, useState, useMemo, useContext } from 'react';
import { Button, Tooltip, Tabs, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ExclamationCircleOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import Table from '@components/Table';
import useSwr from '@hooks/useSwr';
import rolesConstants from '@utils/rolesConstants';
import api from '@services/api';
import { bytesFormat, dateFormat } from '@utils/formatters';
import RoleChecker from '@components/RoleChecker';
import useSearchParams from '@hooks/useSearchParams';
import SessionContext from '@providers/Session/SessionContext';
import ModalCreateAlerts from './ModalCreateAlerts';
import * as S from './styles';

const { TabPane } = Tabs;
const { confirm } = Modal;

const Alerts = () => {
  const { t } = useTranslation(['alerts', 'attributes']);
  const { isMotBusiness } = useContext(SessionContext);

  const [modalVisible, setModalVisible] = useState(false);

  const { setSearchParams, searchParams } = useSearchParams({
    type: 'pagination',
    defaultParams: ['active'],
  });

  const { active, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );
  const { data, mutate } = useSwr(`/service-proxy/alerts`, {
    params: {
      page: page ? Number(page) - 1 : 0,
      size: linesPerPage ?? 20,
      active: active !== 'false',
    },
  });

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/service-proxy/alerts/${id}`);
        message.success(t('msgs.alert-deleted'));
        mutate();
      } catch {
        message.error(t('msgs.alert-deletion-error'));
      }
    },
    [t, mutate],
  );

  const showDelConfirm = useCallback(
    (idToDelete: string, name: string) => {
      confirm({
        title: t('titles.delete-modal', { name }),
        icon: <ExclamationCircleOutlined />,
        content: t('titles.delete-modal-content'),
        centered: true,
        okText: t('buttons.yes'),
        cancelText: t('buttons.no'),
        okButtonProps: { type: 'primary', danger: true },
        onOk: () => handleDelete(idToDelete),
      });
    },
    [handleDelete, t],
  );

  const adminActions = useCallback(
    (text: string, record: any) => (
      <Button
        icon={<DeleteOutlined />}
        danger
        onClick={() => showDelConfirm(record.id, record.name)}
      />
    ),
    [showDelConfirm],
  );

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', align: 'center' },
      {
        title: t('table.eventType'),
        dataIndex: 'eventType',
        align: 'center',
        render: (value: string) => {
          const key =
            value === 'CONTRACT_CONSUMPTION' && isMotBusiness
              ? 'SHARED_PLAN_CONSUMPTION'
              : value;

          return t(`eventType.${key}`);
        },
      },
      {
        title: t('table.field'),
        dataIndex: 'field',
        align: 'center',
        render: (value: string) => t(`field.${value}`),
      },
      {
        title: t('table.thresholdValue'),
        dataIndex: 'thresholdValue',
        align: 'center',
        render: (value: string, record: any) => {
          if (record.field === 'consumptionPercent') {
            return `${value}%`;
          }
          if (
            record.field === 'consumptionTotalAnomaly' ||
            record.field === 'consumptionCurrentDayAnomaly'
          ) {
            return `${Number(value) * 100}%`;
          }
          return bytesFormat(value);
        },
      },
      {
        title: t('attributes:name'),
        dataIndex: 'name',
        align: 'center',
      },
      {
        title: t('attributes:createdAt'),
        dataIndex: 'createdAt',
        align: 'center',
        render: value => value && dateFormat(value),
      },
      ...(active === 'false'
        ? [
            {
              title: t('table.updatedAt'),
              dataIndex: 'updatedAt',
              align: 'center',
              render: (value: string) => value && dateFormat(value),
            },
            {
              title: t('table.updatedBy'),
              dataIndex: 'updatedBy',
              align: 'center',
            },
          ]
        : [
            {
              title: t('attributes:actions'),
              dataIndex: 'actions',
              align: 'center',
              render: adminActions,
            },
          ]),
    ],
    [t, active, adminActions, isMotBusiness],
  );

  return (
    <S.AlertsContainer>
      {modalVisible && (
        <ModalCreateAlerts
          visible={modalVisible}
          mutate={mutate}
          onClose={() => setModalVisible(false)}
        />
      )}
      <Tabs
        type="card"
        defaultActiveKey="true"
        tabBarStyle={{ margin: 0 }}
        activeKey={active}
        onChange={key => {
          setSearchParams({
            active: key,
          });
        }}
      >
        <TabPane key="true" tab={t('visions.active')} />
        <TabPane key="false" tab={t('visions.inactive')} />
      </Tabs>

      <Table
        title={t('titles.alerts')}
        // @ts-ignore
        columns={columns}
        data={data?.content}
        loading={!data?.content}
        tableKey="alerts"
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        extraActions={
          <RoleChecker role={rolesConstants.MANAGE_ALERTS}>
            <Tooltip title={t('buttons.newAlert')}>
              <Button
                icon={<PlusCircleOutlined />}
                onClick={() => setModalVisible(true)}
              />
            </Tooltip>
          </RoleChecker>
        }
      />
    </S.AlertsContainer>
  );
};

export default Alerts;
